import { BOARD_IMAGE_TYPES, BOARD_LIMITS, imageTypeOf } from '@realmery/realm-core';
import { ImageFileError, NOT_A_PICTURE, browserTools, type ImageTools } from '../editor/imageFiles.ts';

/**
 * Getting a picture ready for a board (docs/PARITY-STEP-4-PLAN.md decision 5): told by its
 * bytes, not its name, and decoded by the browser before it's kept. PNG, JPEG and WebP are
 * kept as they are, as the desktop keeps them; GIF and BMP become PNG, the types the
 * desktop names a board's pictures by. At most 50 MB, as on the desktop.
 */
export async function prepareBoardImage(file: Pick<File, 'arrayBuffer'>, tools: ImageTools = browserTools): Promise<Uint8Array> {
  const data = new Uint8Array(await file.arrayBuffer());
  const type = imageTypeOf(data);
  if (!type) throw new ImageFileError(NOT_A_PICTURE);
  const tooLarge = () => new ImageFileError('A picture can be at most 50 MB.');
  if (data.byteLength > BOARD_LIMITS.imageBytes) throw tooLarge();
  let bitmap: ImageBitmap;
  try {
    bitmap = await tools.decode(new Blob([data], { type }));
  } catch {
    throw new ImageFileError('That picture could not be read. It may be damaged.');
  }
  try {
    if (BOARD_IMAGE_TYPES.has(type)) return data;
    const png = await tools.toPng(bitmap);
    if (png.byteLength > BOARD_LIMITS.imageBytes) throw tooLarge();
    return png;
  } finally {
    bitmap.close();
  }
}
